import { EnvironmentProviders, makeEnvironmentProviders, Provider } from '@angular/core';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard, MsalService } from '@azure/msal-angular';
import { InteractionType, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { AppAuthService } from '@trumpf-uxlabs/ng-app-auth';
import { Environment, isSalesforceAuth } from '../../environments/environment.model';
import { createAuthClientConfig } from './auth.config';
import { AuthService } from './auth.service';
import { EntraAuthService } from './entra-auth.service';
import { createMsalConfig, getProtectedResources } from './msal.config';
import { SalesforceAuthService } from './salesforce-auth.service';

export function MSALInstanceFactory(config: Environment): IPublicClientApplication {
    const msalInstance = new PublicClientApplication(createMsalConfig(config));
    msalInstance.initialize();
    return msalInstance;
}

export function MSALGuardConfigFactory(config: Environment) {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: ['openid', 'profile', 'email', ...getProtectedResources(config).api.scopes],
        },
    };
}

export function MSALInterceptorConfigFactory(config: Environment) {
    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap: new Map([[getProtectedResources(config).api.endpoint, getProtectedResources(config).api.scopes]]),
    };
}

function provideEntraAuth(config: Environment): Provider[] {
    return [
        {
            provide: MSAL_INSTANCE,
            useFactory: () => MSALInstanceFactory(config),
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: () => MSALGuardConfigFactory(config),
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: () => MSALInterceptorConfigFactory(config),
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        {
            provide: AuthService,
            useClass: EntraAuthService,
        },
    ];
}

function provideSalesforceAuth(config: Environment): Provider[] {
    return [
        {
            provide: AppAuthService,
            useFactory: () => new AppAuthService(createAuthClientConfig(config.auth)),
        },
        {
            provide: AuthService,
            useClass: SalesforceAuthService,
        },
    ];
}

/**
 * Registers either MSAL (Entra) or @trumpf-uxlabs/ng-app-auth (Salesforce)
 * based on `authenticationType` in the runtime config.
 */
export function provideAuthForConfig(config: Environment): EnvironmentProviders {
    return makeEnvironmentProviders(isSalesforceAuth(config) ? provideSalesforceAuth(config) : provideEntraAuth(config));
}
